﻿import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { FiDownload } from 'react-icons/fi';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useReport } from '@/hooks/useReport';
import { useToast } from '@/components/common/Toast';
import { api } from '@/services/api';
import { config } from '@/config/env';
import { formatDate, parseDate } from '@/utils/dateUtils';
import type { Visit } from '@/hooks/useVisits';
import PageHeader from '@/components/common/PageHeader';
import Spinner from '@/components/common/Spinner';
import InfiniteScrollSentinel from '@/components/common/InfiniteScrollSentinel';
import ConfirmModal from '@/components/common/ConfirmModal';
import VisitFilters from '@/components/visits/VisitFilters';
import type { Filters } from '@/components/visits/VisitFilters';
import VisitTable from '@/components/visits/VisitTable';
import VisitDetailPanel from '@/components/visits/VisitDetailPanel';
import EmailModal from '@/components/visits/EmailModal';
import ExportGeoModal from '@/components/common/ExportGeoModal';

const EMPTY_FILTERS: Filters = { startDate: '', endDate: '', codazienda: '0', codcliente: '0' };

export default function VisitListPage() {
  const { t } = useTranslation('visits');
  const { t: tc } = useTranslation();
  const { showToast } = useToast();
  const { download } = useReport();

  const [deleteTarget, setDeleteTarget] = useState<Visit | null>(null);
  const [detailVisit, setDetailVisit] = useState<Visit | null>(null);
  const [emailVisit, setEmailVisit] = useState<Visit | null>(null);
  const [showExportGeo, setShowExportGeo] = useState(false);
  const filtersRef = useRef<Filters>({ startDate: '', endDate: '', codazienda: '0', codcliente: '0' });

  const { items: visits, loading, loadingMore, hasMore, total, setFetchFn, sentinelRef } =
    useInfiniteScroll<Visit>({ pageSize: config.NUMELEM });

  const buildFetchFn = useCallback((filters: Filters) => {
    return async (page: number, pageSize: number, signal: AbortSignal) => {
      const body: Record<string, unknown> = {};
      if (filters.startDate) body.startDate = filters.startDate + 'T00:00:00Z';
      if (filters.endDate) body.endDate = filters.endDate + 'T23:59:59Z';
      if (filters.codazienda && filters.codazienda !== '0') body.codazienda = filters.codazienda;
      if (filters.codcliente && filters.codcliente !== '0') body.codcliente = filters.codcliente;

      const data = await api.post<{ elenco: Visit[]; totale: number }>(
        'lmeet/risposte-questionario/search-testate',
        body,
        { params: { page, size: pageSize }, signal }
      );
      return { items: data?.elenco || [], total: data?.totale || 0 };
    };
  }, []);

  useEffect(() => {
    setFetchFn(buildFetchFn(EMPTY_FILTERS));
  }, [setFetchFn, buildFetchFn]);

  const handleSearch = useCallback((filters: Filters) => {
    filtersRef.current = filters;
    setFetchFn(buildFetchFn(filters));
  }, [setFetchFn, buildFetchFn]);

  const handleReset = useCallback(() => {
    filtersRef.current = EMPTY_FILTERS;
    setFetchFn(buildFetchFn(EMPTY_FILTERS));
  }, [setFetchFn, buildFetchFn]);

  // Download PDF
  const handleDownload = async (visit: Visit) => {
    const ok = await download(visit.id, visit.versione, visit.ragsociale || '');
    if (!ok) showToast('Errore', 'Errore nel download del report', 'danger');
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await api.del<{ isdeleted: string }>(
        'lmeet/risposte-questionario/' + deleteTarget.id + '/' + deleteTarget.versione
      );
      setDeleteTarget(null);
      if (res?.isdeleted === 'S') {
        showToast(tc('actions.delete'), t('deleteSuccess'), 'success');
        setFetchFn(buildFetchFn(filtersRef.current));
      }
    } catch {
      showToast('Errore', 'Errore durante la cancellazione', 'danger');
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <PageHeader title={t('title')} />
      <div className="container py-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="flex-grow-1"><VisitFilters onSearch={handleSearch} onReset={handleReset} /></div>
          <Button variant="outline-secondary" size="sm" className="ms-2" style={{ whiteSpace: 'nowrap' }}
            onClick={() => setShowExportGeo(true)}>
            <FiDownload className="me-1" /> Export Geo
          </Button>
        </div>
        {loading ? <Spinner text={tc('labels.loading')} /> : (
          <>
            <VisitTable
              visits={visits}
              onView={v => setDetailVisit(v)}
              onDelete={v => setDeleteTarget(v)}
              onDownload={handleDownload}
              onEmail={v => setEmailVisit(v)}
            />
            <InfiniteScrollSentinel
              sentinelRef={sentinelRef}
              loadingMore={loadingMore}
              hasMore={hasMore}
              total={total}
              loadedCount={visits.length}
            />
          </>
        )}
      </div>

      {/* Detail offcanvas */}
      <VisitDetailPanel
        visit={detailVisit}
        show={!!detailVisit}
        onHide={() => setDetailVisit(null)}
        onDelete={v => { setDetailVisit(null); setDeleteTarget(v); }}
        onDownload={handleDownload}
        onSaved={() => { setDetailVisit(null); setFetchFn(buildFetchFn(filtersRef.current)); }}
      />

      {/* Email modal */}
      <EmailModal
        visit={emailVisit}
        show={!!emailVisit}
        onHide={() => setEmailVisit(null)}
      />

      {/* Delete confirm */}
      <ConfirmModal
        show={!!deleteTarget}
        message={t('deleteConfirm')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Export Geo */}
      <ExportGeoModal show={showExportGeo} onHide={() => setShowExportGeo(false)} />
    </>
  );
}

